import { useEffect, useState } from 'react';

import Api from '~/Api';

// Session-wide cache so the geolocation endpoint is hit at most once per session
// and every consumer shares the result. Without this each `useUserCountry`
// instance fired its own `Api.getCountry()` request. The first consumer to
// mount triggers the fetch; the rest — and any later remounts — read the
// resolved value synchronously. Intentionally NOT persisted across sessions, so
// a changed location (e.g. VPN toggled off) is reflected on the next load.
let cachedCountry = '';
let inflightRequest: Promise<string> | null = null;
const subscribers = new Set<(country: string) => void>();

const fetchCountryOnce = (): Promise<string> => {
  if (cachedCountry) return Promise.resolve(cachedCountry);

  if (!inflightRequest) {
    inflightRequest = Api.getCountry()
      .catch(() => 'US')
      .then((country) => {
        cachedCountry = country;
        inflightRequest = null;
        subscribers.forEach((notify) => notify(country));
        return country;
      });
  }

  return inflightRequest;
};

const useUserCountry = (profileCountry = ''): string => {
  const [userCountryCode, setUserCountryCode] = useState<string>(
    profileCountry || cachedCountry,
  );

  useEffect(() => {
    // Caller already knows the country, or it is already resolved/cached.
    if (profileCountry || userCountryCode) return undefined;

    const notify = (country: string): void => setUserCountryCode(country);
    subscribers.add(notify);
    fetchCountryOnce();

    return (): void => {
      subscribers.delete(notify);
    };
  }, [profileCountry, userCountryCode]);

  return profileCountry || userCountryCode;
};

export default useUserCountry;
